import type { ApplicationId, ObjectId, SpatialObject, WorkspaceId } from '@/types';
import type { ApplicationManifest, ApplicationRegistry } from '@/applications';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { fuzzyRank, type FuzzyMatch } from '@/core/search/fuzzy';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useInteractionStore } from '@/store/interactionStore';
import { useWindowStore } from '@/store/windowStore';
import { zoomToObject } from '@/features/camera/cameraNavigation';
import { focusWindow } from '@/features/windows/windowManager';
import { openApplication } from '@/features/windows/windowManager';
import type { CameraAnimator } from '@/features/camera/cameraAnimator';
import { objectType } from '@/core/scene/objectTypes';
import { setCollapsedCommand } from '@/features/objects/objectCommands';
import { isContainer } from '@/types';

export type SearchResult =
  | {
      readonly kind: 'object';
      readonly id: ObjectId;
      readonly workspaceId: WorkspaceId;
      readonly title: string;
      readonly subtitle: string;
      readonly match: FuzzyMatch | null;
    }
  | {
      readonly kind: 'application';
      readonly id: ApplicationId;
      readonly title: string;
      readonly subtitle: string;
      readonly match: FuzzyMatch | null;
    }
  | {
      readonly kind: 'workspace';
      readonly id: WorkspaceId;
      readonly title: string;
      readonly subtitle: string;
      readonly match: FuzzyMatch | null;
    };

interface Indexed {
  readonly result: SearchResult;
  readonly fields: readonly string[];
}

const objectFields = (o: SpatialObject, workspaceName: string, app: ApplicationManifest | undefined): string[] => {
  const extra: string[] = [];
  switch (o.kind) {
    case 'file':
      extra.push(o.fileName, o.mimeType);
      break;
    case 'bookmark':
      extra.push(o.title, o.url);
      break;
    case 'note':
      extra.push(o.content.slice(0, 200));
      break;
    case 'terminal':
      extra.push(o.cwd);
      break;
    default:
      break;
  }
  return [
    o.name,
    o.kind,
    objectType(o.kind).label,
    ...(app ? [app.name] : []),
    ...o.metadata.tags,
    workspaceName,
    ...extra,
    o.id,
  ];
};

/** Builds a flat search index over every workspace, application and workspace name. */
export const buildIndex = (applications: ApplicationRegistry): Indexed[] => {
  const { workspaces } = useWorkspaceStore.getState();
  const out: Indexed[] = [];
  for (const doc of Object.values(workspaces)) {
    out.push({
      result: {
        kind: 'workspace',
        id: doc.workspace.id,
        title: doc.workspace.name,
        subtitle: `workspace · ${Object.keys(doc.objects).length} objects`,
        match: null,
      },
      fields: [doc.workspace.name, 'workspace'],
    });
    for (const o of Object.values(doc.objects)) {
      const app = o.kind === 'window' || o.kind === 'application' ? applications.get(o.applicationId) : undefined;
      out.push({
        result: {
          kind: 'object',
          id: o.id,
          workspaceId: doc.workspace.id,
          title: o.kind === 'file' ? o.fileName : o.name,
          subtitle: `${objectType(o.kind).label.toLowerCase()}${app ? ` · ${app.name}` : ''} · ${doc.workspace.name}`,
          match: null,
        },
        fields: objectFields(o, doc.workspace.name, app),
      });
    }
  }
  for (const app of applications.list()) {
    out.push({
      result: {
        kind: 'application',
        id: app.id,
        title: app.name,
        subtitle: `application · ${app.category}`,
        match: null,
      },
      fields: [app.name, 'application', app.category, app.description],
    });
  }
  return out;
};

export const searchSpatial = (query: string, applications: ApplicationRegistry, limit = 30): SearchResult[] => {
  const index = buildIndex(applications);
  if (!query.trim()) return index.slice(0, limit).map((i) => i.result);
  return fuzzyRank(query, index, (i) => i.fields, limit).map(({ item, match }) => ({ ...item.result, match }));
};

const HIGHLIGHT_MS = 1800;
let highlightTimer: ReturnType<typeof setTimeout> | null = null;

/** Pulses an object's outline briefly; a new highlight replaces the previous one. */
export const highlightObject = (id: ObjectId | null): void => {
  if (highlightTimer) clearTimeout(highlightTimer);
  useInteractionStore.getState().setHighlighted(id);
  if (id) {
    highlightTimer = setTimeout(() => {
      if (useInteractionStore.getState().highlightedId === id) useInteractionStore.getState().setHighlighted(null);
    }, HIGHLIGHT_MS);
  }
};

/**
 * Acts on a chosen result: objects are navigated to, selected (focused if a window)
 * and highlighted — switching workspace first when needed; applications open;
 * workspaces switch.
 */
export const activateResult = (
  result: SearchResult,
  scene: SceneGraph,
  applications: ApplicationRegistry,
  animator: CameraAnimator,
): void => {
  const ws = useWorkspaceStore.getState();
  switch (result.kind) {
    case 'workspace':
      ws.switchWorkspace(result.id);
      return;
    case 'application':
      openApplication(result.id, applications);
      return;
    case 'object': {
      const switching = result.workspaceId !== ws.activeWorkspaceId;
      if (switching) ws.switchWorkspace(result.workspaceId);
      const go = () => {
        // A hit inside a collapsed group is invisible until its ancestors are expanded.
        for (const ancestorId of scene.ancestorsOf(result.id)) {
          const d = useWorkspaceStore.getState().workspaces[result.workspaceId];
          const ancestor = d?.objects[ancestorId];
          const cmd =
            d && ancestor && isContainer(ancestor) && ancestor.collapsed
              ? setCollapsedCommand(d, ancestorId, false)
              : null;
          if (cmd) useWorkspaceStore.getState().dispatch(cmd);
        }
        zoomToObject(scene, result.id, switching ? undefined : animator);
        useSelectionStore.getState().select([result.id]);
        const obj = ws.workspaces[result.workspaceId]?.objects[result.id];
        if (obj?.kind === 'window') focusWindow(result.id);
        else useWindowStore.getState().blur();
        highlightObject(result.id);
      };
      // After a workspace switch the scene graph syncs on the next store notification; defer one tick.
      if (switching) setTimeout(go, 0);
      else go();
    }
  }
};
